import { Directive, ElementRef, Input, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { MatAnchor, MatButton, MatIconButton } from '@angular/material/button';
import { MatMenuItem } from '@angular/material/menu';
import { Permissao } from '@models/enums/permissao';
import { PermissionService } from '@services/permission.service';

/**
 * Diretiva de atributo que desabilita o elemento quando o usuário
 * não possui a permissão informada, mantendo-o visível na tela.
 *
 * @example
 * <button [disableNoPermission]="Permissao.EditarContaCaixa">Criar Conta</button>
 */
@Directive({ selector: '[disableNoPermission]', standalone: false })
export class DisableNoPermissionDirective implements OnInit, OnDestroy {

  @Input() disableNoPermission!: Permissao | boolean;

  private anchorParentClickHandler: ((event: Event) => void) | null = null;
  private anchorParentElement: HTMLElement | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private permissionService: PermissionService,
    @Optional() private matMenuItem: MatMenuItem,
    @Optional() private matButton: MatButton,
    @Optional() private matIconButton: MatIconButton,
    @Optional() private matAnchor: MatAnchor,
  ) {}

  ngOnInit(): void {
    const semPermissao = typeof this.disableNoPermission === 'boolean'
      ? !this.disableNoPermission
      : !this.permissionService.has(this.disableNoPermission);

    if (semPermissao) {
      this.renderer.setAttribute(this.el.nativeElement, 'disabled', 'true');
      this.renderer.setAttribute(this.el.nativeElement, 'title', 'Você não tem permissão para realizar esta ação');
      if (this.matMenuItem) {
        this.matMenuItem.disabled = true;
      }
      if (this.matButton) {
        this.matButton.disabled = true;
      }
      if (this.matIconButton) {
        this.matIconButton.disabled = true;
      }
      if (this.matAnchor) {
        this.matAnchor.disabled = true;
      } else if (this.el.nativeElement.tagName === 'A') {
        // Adiciona classe CSS para visual de desabilitado e pointer-events: none
        this.renderer.addClass(this.el.nativeElement, 'disabled');
        // Listener de captura no pai garante que o evento é interceptado ANTES que
        // qualquer handler do próprio elemento (ex: RouterLink) processe o click
        this.anchorParentElement = this.el.nativeElement.parentElement;
        if (this.anchorParentElement) {
          this.anchorParentClickHandler = (event: Event) => {
            if (this.el.nativeElement.contains(event.target as Node)) {
              event.stopPropagation();
              event.preventDefault();
            }
          };
          this.anchorParentElement.addEventListener('click', this.anchorParentClickHandler, true);
        }
      }
    }
  }

  ngOnDestroy(): void {
    if (this.anchorParentElement && this.anchorParentClickHandler) {
      this.anchorParentElement.removeEventListener('click', this.anchorParentClickHandler, true);
    }
  }
}
